import React from "react";
import type {FunctionComponent} from "react";
import s from "./ComponentLibrary.module.scss";
import {Link, Outlet, RouteObject, useOutlet} from "react-router-dom";
import {router} from "../../App";
import {ComponentLibraryGroup, IComponentLibraryRouteHandle} from "./componentLibraryTypes";

export interface IComponentLibrary {
}

export const ComponentLibrary: FunctionComponent<IComponentLibrary> = ({}) => {
  // List off all components from react router
  const {ungrouped, grouped} = listComponentLibraryRoutes(router.routes);
  const outlet = useOutlet();
  const fallback = "This is a component library. Click a link in the sidebar.";

  // Sort group names alphabetically for consistent rendering order
  const sortedGroupNames = Array.from(grouped.keys()).sort();

  return <div className={s.container}>
    <header>
      Component Library
      <nav/>
    </header>
    <nav className={s.sidebar}>
      {/* Render ungrouped routes first */}
      {ungrouped.map((route, index) => (
        <div key={`ungrouped-${index}`} className={s.routeLink}>
          <Link to={route.path || ""}>
            {route.name}
          </Link>
        </div>
      ))}
      
      {/* Render grouped routes */}
      {sortedGroupNames.map((groupName) => {
        const routes = grouped.get(groupName)!;
        return (
          <div key={groupName} className={s.group}>
            <div className={s.groupHeader}>{groupName}</div>
            {routes.map((route, index) => (
              <div key={`${groupName}-${index}`} className={s.routeLink}>
                <Link to={route.path || ""}>
                  {route.name}
                </Link>
              </div>
            ))}
          </div>
        );
      })}
    </nav>
    <main>
      {outlet ? <Outlet/> : fallback} {/* This is where the nested routes will be rendered */}
    </main>
  </div>;
}

export interface IComponentLibraryRoute {
  path: string;
  name: string;
  group?: ComponentLibraryGroup;
}

export interface IGroupedComponentLibraryRoutes {
  ungrouped: IComponentLibraryRoute[];
  grouped: Map<ComponentLibraryGroup, IComponentLibraryRoute[]>;
}

/**
 * Lists all routes that are children of the "component-library" route. Transforms the path into the actual component name
 * by removing hyphens and capitalizing the first letter of each word following a hyphen. 
 * Groups routes by their handle.group property, with ungrouped routes appearing first.
 * Sorts components alphabetically by name within each group.
 */
export const listComponentLibraryRoutes = (routes: RouteObject[]): IGroupedComponentLibraryRoutes => {
  const ungrouped: IComponentLibraryRoute[] = [];
  const grouped = new Map<ComponentLibraryGroup, IComponentLibraryRoute[]>();

  routes
    .filter(route => route.path == "component-library")
    .forEach((route) => {
      if (route.children) {
        route.children.forEach((childRoute) => {
          if (childRoute.path && childRoute.element) {
            // Name should be the path with each hyphen replaced with the empty string and the first letter of each word following the hyphen capitalized.
            const name = childRoute.path.split("-").map((word) => {
              return word.charAt(0).toUpperCase() + word.slice(1);
            }).join("");

            const handle = childRoute.handle as IComponentLibraryRouteHandle | undefined;
            const group = handle?.group;

            const routeInfo: IComponentLibraryRoute = {
              path: childRoute.path,
              name,
              group
            };

            if (group) {
              // Add to grouped routes
              if (!grouped.has(group)) {
                grouped.set(group, []);
              }
              grouped.get(group)!.push(routeInfo);
            } else {
              // Add to ungrouped routes
              ungrouped.push(routeInfo);
            }
          }
        });
      }
    });

  // Sort ungrouped routes alphabetically
  ungrouped.sort((a, b) => a.name.localeCompare(b.name));

  // Sort routes within each group alphabetically
  grouped.forEach((routes) => {
    routes.sort((a, b) => a.name.localeCompare(b.name));
  });

  return {ungrouped, grouped};
};